import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, auth, onUnauthorized } from "../api/client";
import type { Me, StaffRole } from "../api/types";

interface SessionState {
  me: Me | null;
  loading: boolean;
  error: ApiError | null;
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
  setMe: (me: Me | null) => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const inflight = useRef<Promise<Me | null> | null>(null);

  const refresh = useCallback(async () => {
    if (inflight.current) return inflight.current;
    const p = (async () => {
      try {
        const m = await auth.me();
        setMe(m);
        setError(null);
        return m;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setMe(null);
          setError(null);
        } else if (e instanceof ApiError) {
          setError(e);
        } else {
          setError(new ApiError(0, "unknown", (e as Error)?.message ?? "erro"));
        }
        return null;
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    inflight.current = p;
    return p;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => onUnauthorized(() => setMe(null)), []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setMe(null);
    }
  }, []);

  const value = useMemo<SessionState>(() => ({ me, loading, error, refresh, logout, setMe }), [me, loading, error, refresh, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession fora do SessionProvider");
  return v;
}

/** Sessão garantida (dentro de rota protegida). */
export function useMe(): Me {
  const { me } = useSession();
  if (!me) throw new Error("useMe sem sessão");
  return me;
}

export function isStaff(me: Me | null): boolean {
  return Boolean(me?.user.staffRole);
}

export function staffRole(me: Me | null): StaffRole | null {
  return me?.user.staffRole ?? null;
}

/** RBAC do cliente. A API valida de novo. */
export const can = {
  decideKyb: (r: StaffRole | null) => r === "admin" || r === "compliance",
  decideBeneficiary: (r: StaffRole | null) => r === "admin" || r === "compliance",
  actOnOrders: (r: StaffRole | null) => r === "admin" || r === "ops",
  reconcile: (r: StaffRole | null) => r === "admin" || r === "ops",
  editSettings: (r: StaffRole | null) => r === "admin",
  approveAccess: (r: StaffRole | null) => r === "admin",
  editCompanies: (r: StaffRole | null) => r === "admin",
  retryJobs: (r: StaffRole | null) => r === "admin" || r === "ops",
};

export function companyCanWrite(me: Me | null): boolean {
  return me?.role === "owner" || me?.role === "finance";
}
export function isOwner(me: Me | null): boolean {
  return me?.role === "owner";
}
/** Painel de demonstração: só quando a API está com o parceiro simulado (ela decide; em modo Lumx/Avenia não aparece). */
export function sandboxEnabled(me: Me | null): boolean {
  return Boolean(me?.sandbox);
}
